import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import {
  billingApi, BillingStatus, PlayBillingStatus, playBillingApi, profileApi,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  initializePlayBilling, isPlayStoreReady, managePlaySubscriptions, playPlanPrice,
  purchasePlayPlan, restorePlayPurchases,
} from '../services/playBilling';

function errorMessage(error: unknown): string {
  if (axios.isAxiosError<{ error?: string }>(error)) {
    return error.response?.data?.error || 'Unable to connect to billing. Please try again.';
  }
  return 'Unable to update your subscription. Please try again.';
}

function SubscriptionCard() {
  return Capacitor.isNativePlatform() ? <PlaySubscriptionCard /> : <WebSubscriptionCard />;
}

function PlaySubscriptionCard() {
  const { user, updateUser } = useAuth();
  const [status, setStatus] = useState<PlayBillingStatus | null>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const reloadStatus = useCallback(async () => {
    const { data } = await playBillingApi.refresh();
    setStatus(data);
    const profile = await profileApi.getProfile();
    updateUser({ tier: profile.data.tier, groups: profile.data.groups, sub: profile.data.sub });
  }, [updateUser]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const { data: playStatus } = await playBillingApi.getStatus();
        if (!active) return;
        setStatus(playStatus);
        if (!playStatus.enabled) return;
        const profile = user?.sub ? undefined : await profileApi.getProfile();
        const userId = user?.sub || profile?.data.sub;
        if (!userId) throw new Error('Unable to identify the signed-in account.');
        if (profile) updateUser({ sub: userId });
        await reloadStatus();
        await initializePlayBilling(userId, {
          verified: () => {
            if (!active) return;
            void reloadStatus().then(() => setNotice('Your Google Play subscription is verified.'))
              .catch((err) => setError(errorMessage(err)));
          },
          failed: (message) => { if (active) setError(message); },
        });
        if (active) setReady(isPlayStoreReady());
      } catch (err) {
        if (active) setError(errorMessage(err));
      }
    };
    void load();
    return () => { active = false; };
  }, [user?.sub, updateUser, reloadStatus]);

  const runPlayAction = async (action: 'monthly' | 'annual' | 'restore' | 'manage' | 'refresh') => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      if (action === 'monthly' || action === 'annual') {
        await purchasePlayPlan(action);
        setNotice('Continue in Google Play to complete your purchase.');
      } else if (action === 'restore') {
        await restorePlayPurchases();
        await reloadStatus();
        setNotice('Google Play purchases restored.');
      } else if (action === 'manage') {
        await managePlaySubscriptions();
      } else {
        await reloadStatus();
        setNotice('Subscription status refreshed.');
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const subscription = status?.subscriptions.find((item) =>
    !['SUBSCRIPTION_STATE_EXPIRED', 'SUBSCRIPTION_STATE_PENDING'].includes(item.status)
  );
  const monthlyPrice = playPlanPrice('monthly');
  const annualPrice = playPlanPrice('annual');

  return (
    <div className="profile-card">
      <h3>Plus Subscription</h3>
      {error && <div className="alert alert-error" role="alert">{error}</div>}
      {notice && <div className="alert alert-success" role="status">{notice}</div>}
      {!status && !error && <p role="status">Loading Google Play subscriptions...</p>}
      {status && !status.enabled && (
        <p>Google Play subscriptions are not enabled for this release yet.</p>
      )}
      {status?.enabled && (
        <>
          {status.subscriptions.length === 0 && (
            <p>Choose a plan to unlock the full 7-day outlook and all Plus features.</p>
          )}
          {!ready && !error && <p role="status">Google Play subscription plans are loading.</p>}
          {ready && !monthlyPrice && !annualPrice && (
            <p>Subscription plans are not available in Google Play for this release.</p>
          )}
          {subscription && (
            <p>
              {subscription.plan === 'annual' ? 'Annual' : 'Monthly'} Plus: {subscription.status.replace(/_/g, ' ').toLowerCase()}.
              {' '}{subscription.cancelAtPeriodEnd ? 'Access through' : 'Next renewal'}{' '}
              {new Date(subscription.paidThrough * 1000).toLocaleDateString()}.
            </p>
          )}
          {!subscription && !user?.groups.includes('plus') && (
            <>
              <button className="btn btn-primary btn-full" disabled={busy || !ready || !monthlyPrice}
                onClick={() => runPlayAction('monthly')}>
                Subscribe monthly{monthlyPrice ? ` - ${monthlyPrice}` : ''}
              </button>
              <button className="btn btn-secondary-dark btn-full" disabled={busy || !ready || !annualPrice}
                onClick={() => runPlayAction('annual')}>
                Subscribe annually{annualPrice ? ` - ${annualPrice}` : ''}
              </button>
            </>
          )}
          <button className="btn btn-secondary-dark btn-full" disabled={busy || !ready}
            onClick={() => runPlayAction('restore')}>Restore purchases</button>
          {status.subscriptions.length > 0 && (
            <button className="btn btn-secondary-dark btn-full" disabled={busy || !ready}
              onClick={() => runPlayAction('manage')}>Manage subscription</button>
          )}
          <button className="btn btn-secondary-dark btn-full" disabled={busy}
            onClick={() => runPlayAction('refresh')}>{busy ? 'Please wait...' : 'Refresh subscription'}</button>
        </>
      )}
    </div>
  );
}

function WebSubscriptionCard() {
  const { updateUser } = useAuth();
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let active = true;
    const returned = new URLSearchParams(window.location.search).get('billing');
    const load = async () => {
      try {
        const { data } = returned === 'success' || returned === 'return'
          ? await billingApi.refresh() : await billingApi.getStatus();
        if (!active) return;
        setStatus(data);
        if (returned === 'success' || returned === 'return') {
          const profile = await profileApi.getProfile();
          if (!active) return;
          updateUser({ tier: profile.data.tier, groups: profile.data.groups });
          setNotice(profile.data.tier === 'plus'
            ? 'Your Plus access is confirmed.'
            : 'Payment is not confirmed yet. Use Refresh subscription to check again.');
        } else if (returned === 'canceled') {
          setNotice('Checkout was canceled. Your plan has not changed.');
        }
      } catch (err) {
        if (active) setError(errorMessage(err));
      }
    };
    load();
    return () => { active = false; };
  }, [updateUser]);

  const runAction = async (action: 'monthly' | 'annual' | 'portal' | 'refresh') => {
    setBusy(true);
    setError('');
    try {
      if (action === 'refresh') {
        const { data } = await billingApi.refresh();
        setStatus(data);
        const profile = await profileApi.getProfile();
        updateUser({ tier: profile.data.tier, groups: profile.data.groups });
        setNotice('Subscription status refreshed.');
      } else {
        const { data } = action === 'portal'
          ? await billingApi.portal() : await billingApi.checkout(action);
        const url = new URL(data.url);
        const expectedHost = action === 'portal' ? 'billing.stripe.com' : 'checkout.stripe.com';
        if (url.protocol !== 'https:' || url.hostname !== expectedHost) {
          throw new Error('Unexpected billing redirect');
        }
        window.location.assign(url.href);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (status && !status.enabled) return null;
  const subscription = status?.subscriptions.find((item) =>
    !['canceled', 'incomplete_expired'].includes(item.status)
  );

  return (
    <div className="profile-card">
      <h3>Plus Subscription</h3>
      {status?.enabled && <p className="form-hint">Sandbox testing only. No real payments are collected.</p>}
      {error && <div className="alert alert-error" role="alert">{error}</div>}
      {notice && <div className="alert alert-success" role="status">{notice}</div>}
      {!status && !error && <p role="status">Loading subscription...</p>}
      {!status && error && (
        <button className="btn btn-secondary-dark" onClick={async () => {
          setError('');
          try { setStatus((await billingApi.getStatus()).data); }
          catch (err) { setError(errorMessage(err)); }
        }}>Retry</button>
      )}
      {status?.deleting ? (
        <p>Account deletion is in progress. Subscription changes are disabled.</p>
      ) : status && (
        <>
          {status.complimentary && <p>You have complimentary Plus access. No purchase is required.</p>}
          {subscription && (
            <p>
              {subscription.plan === 'annual' ? 'Annual' : 'Monthly'} Plus: {subscription.status.replace('_', ' ')}.
              {' '}{subscription.cancelAtPeriodEnd ? 'Scheduled to end' : 'Current period ends'}{' '}
              {new Date(subscription.paidThrough * 1000).toLocaleDateString()}.
              {subscription.cancelAtPeriodEnd && ' Plus remains available through the paid period while the subscription is active.'}
            </p>
          )}
          {!status.complimentary && !subscription && status.checkoutReady && (
            <>
              <p>$1.99/month or $14.99/year. Both include the full 7-day outlook and all Plus features. No free trial.</p>
              <p className="form-hint">Automatically renews until canceled. Prices exclude applicable taxes.
                Tax collection is not enabled in this sandbox integration.</p>
              <button className="btn btn-primary btn-full" disabled={busy}
                onClick={() => runAction('monthly')}>Subscribe monthly - $1.99/month</button>
              <button className="btn btn-secondary-dark btn-full" disabled={busy}
                onClick={() => runAction('annual')}>Subscribe annually - $14.99/year</button>
            </>
          )}
          {!status.checkoutReady && <p>Checkout is awaiting Stripe webhook setup.</p>}
          {status.subscriptions.length > 0 && (
            <button className="btn btn-secondary-dark btn-full" disabled={busy}
              onClick={() => runAction('portal')}>Manage subscription</button>
          )}
          <button className="btn btn-secondary-dark btn-full" disabled={busy || !status.checkoutReady}
            onClick={() => runAction('refresh')}>{busy ? 'Please wait...' : 'Refresh subscription'}</button>
        </>
      )}
    </div>
  );
}

export default SubscriptionCard;
